/**
 * 本机文件夹的选择与识别（仅 macOS 客户端）。
 *
 * # 为什么是一个 adapter，而不是在组件里 `invoke()`
 *
 * 因为 Web 上**根本不存在** `invoke`。组件里直接调的话，
 * 那行代码会随组件一起进 Web 产物，然后靠一个 `if` 挡住——
 * 而一个靠 `if` 挡住的本机能力，迟早会被谁把 `if` 挪走。
 *
 * 这一层的规矩是：**拿不到原生那一侧就回 `null`，不降级、不模拟、
 * 不用浏览器的目录 API 顶上。** 回 null 之后调用方不渲染任何东西，
 * 而不是渲染一个点不动的按钮。
 */
import { canPickLocalFolder, type Surface } from './platform'
import type { FolderVerdict } from './folder'

/** Tauri v2 注入的调用口。名字与 `platform.ts` 里那个判据同源。 */
interface TauriInternals {
  readonly invoke?: (cmd: string, args?: Record<string, unknown>) => Promise<unknown>
}

function internals(win: unknown = globalThis): TauriInternals | null {
  if (typeof win !== 'object' || win === null) return null
  const v = (win as Record<string, unknown>)['__TAURI_INTERNALS__']
  return typeof v === 'object' && v !== null ? (v as TauriInternals) : null
}

/** 一次选择的结果。**path 只在设备侧存在**，不进任何请求体。 */
export interface PickedFolder {
  readonly path: string
  readonly verdict: FolderVerdict
  readonly deviceRef: string
  readonly fingerprint: string
}

/**
 * 打开系统目录选择器，识别它，回一次完整结果。
 *
 * 三步都在原生那一侧：选（`plugin:dialog|open`）、认（`folder_inspect`）、
 * 算指纹（`folder_identity`）。**指纹绝不在这里算**——
 * 在 JS 里算的话，路径会先进 JS 内存再进网络那一层，
 * 而 PRD §3.6 要的是它压根不出设备。
 *
 * 用户取消选择时回 `null`，与「没有原生那一侧」同一个返回值——
 * 两者对调用方是同一件事：**什么都别做**。
 */
export async function pickLocalFolder(
  surface: Surface,
  create: boolean,
): Promise<PickedFolder | null> {
  if (!canPickLocalFolder(surface)) return null
  const t = internals()
  if (!t?.invoke) return null

  const chosen = await t.invoke('plugin:dialog|open', {
    options: { directory: true, multiple: false, canCreateDirectories: create },
  })
  if (typeof chosen !== 'string' || chosen === '') return null

  const verdict = (await t.invoke('folder_inspect', { path: chosen })) as FolderVerdict
  const identity = (await t.invoke('folder_identity', { path: chosen })) as [string, string]
  const deviceRef = (await t.invoke('folder_device_ref')) as string

  return {
    path: chosen,
    verdict,
    deviceRef,
    fingerprint: identity[0],
  }
}
